import { useMutation, useQuery } from '@apollo/client';
import {
  AddCompanyModal,
  LoadingSpinner,
  Table,
  Tooltip,
  UPDATE_CURRENT_USER,
  GET_COMPANIES_USER_BELONGS_TO,
  DELETE_COMPANY,
  useAuthUser,
  ConfirmationModal,
} from '@employ-engage/common-components';
import {
  BasicTableDataRow,
  RolesEnum,
  TableHeaderItem,
  TableCellContentAlignEnum,
  Company,
} from '@employ-engage/data';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const CompanyPicker = () => {
  const { t } = useTranslation(['common']);
  const [companyToEdit, setCompanyToEdit] = useState<Company | null>(null);
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] =
    useState<boolean>(false);
  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [isDeleteCompanyModalOpen, setIsDeleteCompanyModalOpen] =
    useState<boolean>(false);
  const { user, hasRole } = useAuthUser();
  const {
    data: { companiesUserBelongsTo = [] } = {},
    loading: isLoading,
    refetch: refetchCompaniesUserBelongsTo,
  } = useQuery(GET_COMPANIES_USER_BELONGS_TO, {
    variables: { userId: user?.id },
    fetchPolicy: 'cache-and-network',
  });
  const [updateCurrentUser] = useMutation(UPDATE_CURRENT_USER);
  const [deleteCompany] = useMutation(DELETE_COMPANY);
  const navigate = useNavigate();

  const headerList: TableHeaderItem[] = [
    {
      id: 'friendlyCompanyId',
      headerLabel: t('Company ID'),
    },
    {
      id: 'name',
      headerLabel: t('Company Name'),
    },
  ];

  if (hasRole(RolesEnum.SUPER_ADMIN)) {
    headerList.push({
      id: 'owner',
      headerLabel: t('Owner'),
    });
  }

  headerList.push({
    id: 'actions',
    headerLabel: t('Actions'),
    alignment: TableCellContentAlignEnum.CENTER,
  });

  if (isLoading) {
    return <LoadingSpinner spinnerId="company-picker-loading-2" />;
  }

  return (
    <>
      <Table
        onClickRow={async (company) => {
          try {
            const { data } = await updateCurrentUser({
              variables: {
                input: {
                  currentCompanyId: company.id,
                },
              },
            });

            if (data?.updateCurrentUser) {
              // Navigate to the user home page or refresh the current page
              navigate(`/${company?.friendlyCompanyId}/admin/dashboard`, {});
            }
          } catch (error) {
            console.error('Error updating current user:', error);
          }
        }}
        label={t('Select a Company')}
        headerList={headerList}
        dataList={companiesUserBelongsTo as unknown as BasicTableDataRow[]}
        mapData={(company) => {
          return {
            id: company.id,
            friendlyCompanyId: company.friendlyCompanyId,
            name: (
              <div className="flex items-center space-x-1">
                <span>{company.name}</span>
              </div>
            ),
            owner: company.owner
              ? `${company.owner.displayName} <${company.owner?.email}>`
              : '',
            actions: (
              <div className="flex justify-center items-center space-x-2">
                <Tooltip label={t('Select this company')}>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompanyToEdit(company as Company);
                      setIsAddCompanyModalOpen(true);
                    }}
                    className="justify-center items-center"
                  >
                    <FontAwesomeIcon icon="pen-to-square" />
                  </span>
                </Tooltip>
                {companiesUserBelongsTo.length > 1 && (
                  <Tooltip label={t('Select this company')}>
                    <span
                      onClick={async (e) => {
                        e.stopPropagation();

                        setCompanyToDelete(company as Company);
                        setIsDeleteCompanyModalOpen(true);
                      }}
                      className="justify-center items-center"
                    >
                      <FontAwesomeIcon icon="trash" />
                    </span>
                  </Tooltip>
                )}
              </div>
            ),
          };
        }}
        actionItems={
          <Tooltip label={t('Add Company')}>
            <button
              onClick={() => setIsAddCompanyModalOpen(true)}
              className="w-10 h-10 text-center align-middle rounded-xl bg-light-primary hover:bg-light-primaryAccent dark:bg-dark-primary hover:dark:bg-dark-primaryAccent"
            >
              <i className="fa-solid fa-plus text-light-primaryBackground dark:text-custom-textColor text-lg" />
            </button>
          </Tooltip>
        }
      />
      <AddCompanyModal
        open={isAddCompanyModalOpen}
        setOpen={setIsAddCompanyModalOpen}
        companySelectedForEditing={companyToEdit}
        setCompanySelectedForEditing={setCompanyToEdit}
        refetchCompaniesUserBelongsTo={refetchCompaniesUserBelongsTo}
      />
      <ConfirmationModal
        text={t(
          'Are you sure you want to delete the {{companyName}} company?',
          { companyName: companyToDelete?.name }
        )}
        confirmButtonText={t('Delete Company')}
        warningText={t('This action cannot be undone.')}
        open={isDeleteCompanyModalOpen}
        setOpen={setIsDeleteCompanyModalOpen}
        onConfirm={async () => {
          if (companyToDelete) {
            try {
              await deleteCompany({
                variables: { id: companyToDelete.id },
              });
              refetchCompaniesUserBelongsTo();
              setCompanyToDelete(null);
              setIsDeleteCompanyModalOpen(false);
            } catch (error) {
              console.error('Error deleting company:', error);
            }
          }
        }}
        onCancel={() => setIsDeleteCompanyModalOpen(false)}
      ></ConfirmationModal>
    </>
  );
};

export default CompanyPicker;
